import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@heroui/react';
import { Icon } from '@iconify/react';
import { cn, getNodeTestProps } from '@/utils';
import { PIN_LENGTH, PIN_PAD_KEYS, type PinPadAction } from './pinPadMeta';

type PinPadProps = {
    /** The PIN entered so far — drives the filled progress dots. */
    pin: string;
    /** Number of digits (and dots). Defaults to the shared `PIN_LENGTH`. */
    length?: number;
    onDigit: (digit: string) => void;
    onBackspace: () => void;
    /** Blocks all keys (mid-verify, or after a successful co-sign). */
    isDisabled?: boolean;
    /** Tints the dots in the danger colour after a failed attempt. */
    hasError?: boolean;
    /** The key whose physical twin was just pressed (`usePinPhysicalKeys`) — flashed briefly so the two inputs read as one. */
    flashedKey?: string | PinPadAction | null;
};

/**
 * Render-only kiosk PIN pad: masked progress dots above a touch-friendly
 * `1`-`9` / `0` / backspace grid. Carries no identity card or submit logic — the
 * owning hook decides when the entry is complete. Shared between the seller
 * login and the shift discrepancy co-sign.
 *
 * The digit and backspace buttons are out of the Tab order — keyboard parity
 * comes from `usePinPhysicalKeys` feeding this same `onDigit`/`onBackspace`
 * path, not from tabbing across the pad — but stay fully clickable and
 * touchable. Each one flashes briefly when `flashedKey` names it, so a
 * physical keystroke visibly registers even though the digits are masked.
 */
export const PinPad: React.FC<PinPadProps> = ({
    pin,
    length = PIN_LENGTH,
    onDigit,
    onBackspace,
    isDisabled = false,
    hasError = false,
    flashedKey = null,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div className="flex flex-col items-center gap-6">
            <div
                className="flex items-center gap-3"
                {...getNodeTestProps('pin-pad-dots')}
            >
                {Array.from({ length }).map((_, index) => (
                    <span
                        className={cn(
                            'size-3.5 rounded-full border transition',
                            index < pin.length
                                ? 'border-accent bg-accent'
                                : 'border-border bg-transparent',
                            hasError && 'border-danger',
                        )}
                        // The dots are fixed positional slots, not reorderable.
                        key={index}
                    />
                ))}
            </div>

            <div className="mx-auto grid w-full max-w-[260px] grid-cols-3 justify-items-center gap-3">
                {PIN_PAD_KEYS.map((key, index) => {
                    if (key === null) {
                        return (
                            <div className="size-16" key={`empty-${index}`} />
                        );
                    }
                    if (key === 'backspace') {
                        return (
                            <Button
                                aria-label={t('sellerLogin.backspace')}
                                className="size-16 rounded-full"
                                {...getNodeTestProps('pin-pad-backspace')}
                                excludeFromTabOrder
                                isDisabled={isDisabled || pin.length === 0}
                                isIconOnly
                                key="backspace"
                                onPress={onBackspace}
                                variant={
                                    flashedKey === 'backspace'
                                        ? 'primary'
                                        : 'ghost'
                                }
                            >
                                <Icon
                                    className="size-6 text-muted"
                                    icon="solar:backspace-linear"
                                />
                            </Button>
                        );
                    }
                    return (
                        <Button
                            className="size-16 rounded-full text-lg tabular-nums"
                            {...getNodeTestProps(`pin-pad-key-${key}`)}
                            excludeFromTabOrder
                            isDisabled={isDisabled}
                            isIconOnly
                            key={key}
                            onPress={() => onDigit(key)}
                            variant={flashedKey === key ? 'primary' : 'outline'}
                        >
                            {key}
                        </Button>
                    );
                })}
            </div>
        </div>
    );
};
